import React from 'react';
import { Alert, Box, Checkbox, FormControlLabel, TextField, Typography } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CustomButton from '../buttons/CustomButton';
import ColorPalette from '../../colorpalette/ColorPalette';
import { ConsentCard } from '../../pages/patient/components/PatientDeleteSteps';
import ExportJobStatus from '../company-data-export/ExportJobStatus';
import { getDownloadParts, isExportActive, isExportDownloadable } from '../company-data-export/exportJob';
import { CLOSURE_GRACE_DAYS } from './companyClosure';

/**
 * Passos do diálogo "Encerrar conta" (CompanyClosureDialog). Só apresentação:
 * o estado e as chamadas de API ficam no diálogo.
 * Especificação: docs/features/lgpd-eliminacao/README.md (checkpoint 4)
 */

const TEXT_SX = { color: 'var(--color-text-dark)', lineHeight: 1.5 };
const LIGHT_TEXT_SX = { color: 'var(--color-text-light)', lineHeight: 1.5 };

const LIGHT_BOX_SX = {
    border: `1px solid ${ColorPalette.border}`,
    borderRadius: '8px',
    backgroundColor: '#FAFAFA',
    padding: '10px 14px',
};

const ERASED_CARD_SX = { ...LIGHT_BOX_SX, borderColor: '#FFCDD2', backgroundColor: '#FFF8F8' };
const KEPT_CARD_SX = { ...LIGHT_BOX_SX, borderColor: ColorPalette.blueLight3, backgroundColor: '#F8FAFF' };

function StepTitle({ children }) {
    return (
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--color-text-dark)' }}>
            {children}
        </Typography>
    );
}

function ItemList({ items }) {
    return (
        <Box component="ul" sx={{ margin: 0, paddingLeft: '18px' }}>
            {items.map((item) => (
                <Typography key={item} component="li" variant="body2" sx={{ ...TEXT_SX, overflowWrap: 'anywhere' }}>
                    {item}
                </Typography>
            ))}
        </Box>
    );
}

// accessUntil: texto de até quando a conta segue usável (calculado no diálogo).
// scheduledDate: data prevista da eliminação (dd/mm/aaaa, estimada; o servidor confirma).
export function ClosureSummaryStep({ accessUntil, scheduledDate }) {
    const untilDeadline = [
        accessUntil,
        `A carência de ${CLOSURE_GRACE_DAYS} dias começa hoje. Se o período já pago terminar depois dela, a eliminação fica para o fim dele. Nesse período dá para exportar os dados e, para manter a conta, assinar um plano.`,
        'Enquanto a conta ainda estiver funcionando, dá para desistir pelo botão "Manter conta".',
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <StepTitle>O que vai acontecer</StepTitle>

            <Box sx={KEPT_CARD_SX}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: ColorPalette.blueDark, marginBottom: '4px' }}>
                    Até o fim da carência
                </Typography>
                <ItemList items={untilDeadline} />
            </Box>

            <Box sx={ERASED_CARD_SX}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#C62828', marginBottom: '4px' }}>
                    Depois da carência
                </Typography>
                <ItemList
                    items={[
                        'A equipe da Gabriela elimina a conta e todos os dados: pacientes, agenda, financeiro, documentos, anexos e usuários.',
                        'A eliminação não tem volta. Depois dela, não será possível recuperar nem exportar os dados.',
                    ]}
                />
            </Box>

            {scheduledDate && (
                <Typography variant="body2" sx={TEXT_SX}>
                    Data prevista para a eliminação: <strong>{scheduledDate}</strong>. Você receberá avisos por e-mail antes disso.
                </Typography>
            )}
        </Box>
    );
}

// job: exportação completa mostrada (a criada aqui ou a mais recente da conta);
// starting: pedido de criação em andamento; autoDownload: job criado neste
// diálogo, a página baixa sozinha ao terminar; error: recusa do create (texto);
// downloadedIndexes: partes do job já baixadas nesta abertura do diálogo
// (o Avançar só libera com todas); onDownload(index): clique em Baixar.
export function ClosureExportStep({
    job,
    starting,
    autoDownload,
    error,
    includeAttachments,
    attachmentsAvailable,
    attachmentsLabel,
    downloadedIndexes,
    onIncludeAttachmentsChange,
    onExport,
    onDownload,
}) {
    const active = starting || isExportActive(job);
    const downloadable = isExportDownloadable(job);
    const failed = job?.status === 'error';
    const partCount = downloadable ? Math.max(getDownloadParts(job).length, 1) : 0;
    const downloadedCount = downloadedIndexes.length;
    const downloadDone = downloadable && downloadedCount >= partCount;

    let downloadHint = 'Clique em Baixar para liberar o botão Avançar.';
    if (downloadDone) downloadHint = 'Download feito. Confira o arquivo e guarde em local seguro; o botão Avançar está liberado.';
    else if (partCount > 1) downloadHint = `Baixe todas as ${partCount} partes para liberar o botão Avançar: ${downloadedCount} de ${partCount} baixadas.`;

    let buttonLabel = 'Exportar dados agora';
    if (active) buttonLabel = 'Exportando...';
    else if (downloadable) buttonLabel = 'Exportar de novo';
    else if (failed) buttonLabel = 'Tentar novamente';

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <StepTitle>Exporte os dados antes de encerrar</StepTitle>

            <Typography variant="body2" sx={TEXT_SX}>
                A exportação completa gera a cópia de tudo o que está na conta: pacientes, agenda, financeiro,
                documentos e, se marcado, anexos e fotos. Depois da eliminação a Gabriela não terá mais esses
                dados, por isso o botão Avançar só libera depois que você baixar o arquivo (todas as partes, se
                houver mais de uma). É a mesma exportação do card "Exportar meus dados": o arquivo fica no
                histórico desta página por 24 horas.
            </Typography>

            <Alert severity="info">
                Se a sua atividade é da área da saúde, a lei exige guardar os prontuários por no mínimo
                20 anos (Lei 13.787/2018). Essa responsabilidade passa a ser sua, com base no arquivo exportado.
            </Alert>

            <Box sx={{ ...LIGHT_BOX_SX, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1.5,
                    }}
                >
                    <FormControlLabel
                        sx={{ margin: 0, minWidth: 0 }}
                        control={
                            <Checkbox
                                size="small"
                                checked={includeAttachments}
                                disabled={!attachmentsAvailable || active}
                                onChange={(e) => onIncludeAttachmentsChange(e.target.checked)}
                            />
                        }
                        label={
                            <Typography variant="body2" sx={TEXT_SX}>
                                {`Incluir anexos e fotos${attachmentsLabel || ''}`}
                            </Typography>
                        }
                    />
                    <CustomButton
                        label={buttonLabel}
                        style="primary"
                        variant="contained"
                        icon={<FileDownloadOutlinedIcon fontSize="small" />}
                        loading={starting}
                        enabled={!active}
                        onClick={onExport}
                    />
                </Box>

                {error && (
                    <Alert severity="error" sx={{ width: '100%', boxSizing: 'border-box' }}>
                        {error}
                    </Alert>
                )}

                <ExportJobStatus
                    job={job}
                    starting={starting}
                    autoDownload={autoDownload}
                    onDownload={onDownload}
                    downloadedIndexes={downloadedIndexes}
                />

                {downloadable && (
                    <Typography variant="body2" sx={downloadDone ? TEXT_SX : { ...TEXT_SX, fontWeight: 600 }}>
                        {downloadHint}
                    </Typography>
                )}

                {!active && !downloadable && !failed && !error && (
                    <Typography variant="body2" sx={LIGHT_TEXT_SX}>
                        O arquivo é gerado em segundo plano e pode levar alguns minutos. Se você continuar nesta
                        tela, o download começa sozinho ao terminar; o arquivo também fica no histórico desta
                        página por 24 horas.
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

export function ClosureConfirmStep({
    companyName,
    exportChecked,
    eliminationChecked,
    reason,
    typedName,
    disabled,
    onExportChange,
    onEliminationChange,
    onReasonChange,
    onTypedNameChange,
}) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <StepTitle>Confirmação</StepTitle>

            <ConsentCard
                checked={exportChecked}
                disabled={disabled}
                onChange={onExportChange}
                label="Baixei a exportação completa e guardei em local seguro"
            />
            <ConsentCard
                checked={eliminationChecked}
                disabled={disabled}
                onChange={onEliminationChange}
                label="Entendo que após a carência a conta e todos os dados serão eliminados"
            />

            <TextField
                size="small"
                label="Motivo do encerramento (opcional)"
                value={reason}
                onChange={(e) => onReasonChange(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                disabled={disabled}
                inputProps={{ maxLength: 1000 }}
                InputLabelProps={{ shrink: true }}
            />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, marginTop: 0.5 }}>
                <Typography variant="body2" sx={TEXT_SX}>
                    Para liberar o botão, digite o nome da empresa exatamente como está no cadastro.
                </Typography>
                <Typography variant="body2" sx={{ ...TEXT_SX, overflowWrap: 'anywhere' }}>
                    Nome esperado: <strong>{companyName}</strong>
                </Typography>
                <TextField
                    size="small"
                    label="Nome da empresa"
                    placeholder={companyName}
                    value={typedName}
                    onChange={(e) => onTypedNameChange(e.target.value)}
                    fullWidth
                    disabled={disabled}
                    InputLabelProps={{ shrink: true }}
                />
            </Box>
        </Box>
    );
}
